import { useState, useEffect } from "react";
import "@/App.css";
import { Play, Mail, Phone, Menu, X, ArrowRight, MessageCircle, ChevronDown, Instagram } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";

// Constants
const WHATSAPP_NUMBER = "+17866229008";
const EMAIL = "koner@privamiami.com";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

// Images
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/fq0t2l28_20260313_014527979_iOS.jpg",
  band: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/5ilehsnr_image.png",
  performance: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/whkvoh5o_20260313_014404765_iOS.jpg",
};

// Videos - Using official Gipsy Kings videos
const VIDEOS = [
  {
    id: 1,
    title: "Bamboléo - Live Performance",
    thumbnail: IMAGES.performance,
    youtubeId: "ZTmsvgEXNBs",
    type: "youtube",
  },
  {
    id: 2,
    title: "Live Performance",
    thumbnail: IMAGES.hero,
    externalUrl: "https://www.instagram.com/reel/DV5JS_IjEpt/?igsh=MTZ3ejk0YzN6YjVsYg==",
    type: "instagram",
  },
  {
    id: 3,
    title: "Live Performance",
    thumbnail: IMAGES.band,
    externalUrl: "https://www.instagram.com/reel/DW0YIGFAkTA/?igsh=MXdmcjE0cTN6dXVmNg%3D%3D",
    type: "instagram",
  },
];

// Event Types
const EVENT_TYPES = [
  {
    id: 1,
    title: "Luxury Weddings",
    description: "Create unforgettable moments with passionate flamenco rhythms on your special day",
    image: "https://images.pexels.com/photos/169198/pexels-photo-169198.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  },
  {
    id: 2,
    title: "Private Events",
    description: "Exclusive performances for intimate gatherings and VIP celebrations",
    image: "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  },
  {
    id: 3,
    title: "Corporate Events",
    description: "Elevate your corporate function with world-class entertainment",
    image: "https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  },
];

// Timeline Data for Kıvanç Öner
const TIMELINE_DATA = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/r1kit7yu_image.png",
    description: "Flamenco guitar artist Kıvanç Öner was born in Istanbul.",
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/ddlu5d3z_image.png",
    description: "1996 | Washington D.C. — Kıvanç Öner met Canut Reyes (Gipsy Kings), who shaped his musical career and whom he greatly admired.",
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/bolgbybs_image.png",
    description: "1997 | Virginia — Kıvanç Öner's first concert in America.",
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/uxoejgdn_image.png",
    description: "1997 | Miami — Kıvanç Öner exchanging CDs with famous musician Alex Fox in Miami Beach.",
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/f8ngiszo_image.png",
    description: "1997 | Washington — The Gipsy Kings tour bus after the Washington Concert during US Tour 97, where Kıvanç Öner performed with the Gipsy Kings. In the photo: Kıvanç Öner, Nicolas Reyes, Canut Reyes, and Tonino Baliardo.",
  },
  {
    id: 6,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/oalxwsiq_image.png",
    description: "1998 | Washington D.C. — Kıvanç Öner and Ricardo Marlow, one of America's finest flamenco guitarists, formed their own Flamenco Duo and performed for a wide audience including celebrities like Bill Clinton, Hillary Clinton, and Michael Jordan.",
  },
  {
    id: 7,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/8vudbnic_image.png",
    description: "1998 | Washington D.C. — Kıvanç Öner introducing Ricardo Marlow, his music partner, to Gipsy Kings members Andre Reyes and Michael Baliardo.",
  },
  {
    id: 8,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/aqe1n41e_image.png",
    description: "1998 | Virginia — Kıvanç Öner and Canut Reyes from the Gipsy Kings preparing before a concert at Ritz Carlton Virginia.",
  },
  {
    id: 9,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/mqhsckvs_image.png",
    description: "1998 | Washington D.C. — Kıvanç Öner concert at JW Marriott, organized by the Turkish American Association as part of the Atatürk Ball.",
  },
  {
    id: 10,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/48iczgck_image.png",
    description: "1999 | New York — Kıvanç Öner and Canut Reyes at Radio City Music Hall after a concert.",
  },
  {
    id: 11,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/fm1r6yax_image.png",
    description: "2000 | Northwest Washington, D.C. — Kıvanç Öner and Ricardo Marlow are on stage with world-renowned percussionist Giovanni Hidalgo.",
  },
  {
    id: 12,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/qqu1dccy_ChatGPT%20Image%20Apr%2014%2C%202026%2C%2003_59_44%20AM.png",
    description: "2001 | Virginia — Kıvanç Öner and Ricardo Marlow perform an Acoustic Spanish Night program at La Tasca.",
  },
  {
    id: 13,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/z14g1829_image.png",
    description: "2001 | Washington, D.C. — Kıvanç Öner together with the legendary guitarist Paco de Lucía, who inspired his love for the guitar.",
  },
  {
    id: 14,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/e00oivq3_ChatGPT%20Image%20Apr%2014%2C%202026%2C%2004_09_27%20AM.png",
    description: "2001 | Washington D.C. — After the Paco de Lucía concert, Ricardo Marlow and Kıvanç Öner are enjoying time together with Rubem Dantas, percussionist of the Paco de Lucía Sextet, and the famous flamenco dancer Joaquín Grilo.",
  },
  {
    id: 15,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/8q9nrp9f_image.png",
    description: "2001 | Orlando, Florida — After the Gipsy Kings concert, Kıvanç Öner and Canut Reyes celebrate at the Four Seasons Hotel.",
  },
  {
    id: 16,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/wmxvwckz_image.png",
    description: "2001 | Adams Morgan, D.C. — Kıvanç Öner is performing at Cities Privé Club with Mario Cesar and Jose Oretea.",
  },
  {
    id: 17,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/79hn8wvn_image.png",
    description: "2002 | Adams Morgan, D.C. — Kıvanç Öner together with Carles Benavent, bassist of the Paco de Lucía Sextet.",
  },
  {
    id: 18,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/ioxk6t16_image.png",
    description: "2003 | Bethesda, Maryland — Kıvanç Öner was invited as a guest artist to events organized by the famous television producer John Walsh, one of his audience members.",
  },
  {
    id: 19,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/2ktvb7bb_ChatGPT%20Image%20May%2011%2C%202026%2C%2009_44_27%20PM.png",
    description: "2005 | Virginia — Kıvanç Öner backstage with Gipsy Kings at Wolf Trap National Park for the Performing Arts. The members of Gipsy Kings in the photo are: Canut Reyes, Nicolas Reyes, Pablo Reyes, Andre Reyes, Patchai Reyes, Tonino Baliardo, and Diego Baliardo.",
  },
  {
    id: 20,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/ug24crkf_image.png",
    description: "2006 | Washington DC — Kıvanç Öner and Mario Cesar Oretea performing at Ozio DC Lounge.",
  },
  {
    id: 21,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/ki9pi6ph_ChatGPT%20Image%20May%2011%2C%202026%2C%2009_49_46%20PM.png",
    description: "2007 | District of Columbia — Kıvanç Öner and Hüsnü Şenlendirici performing at Left Bank Lounge.",
  },
  {
    id: 22,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/g5s737r6_ChatGPT%20Image%20May%2011%2C%202026%2C%2009_52_47%20PM.png",
    description: "2009 | Bodrum — Kıvanç Öner presents the concert of Gipsy Kings Heritage (children of the Gipsy Kings family), for which he served as manager, in Bodrum.",
  },
  {
    id: 23,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/uue0hlvm_ChatGPT%20Image%20May%2011%2C%202026%2C%2009_59_32%20PM.png",
    description: "2010 | Arles, France — Kıvanç Öner being hosted at the home of Canut Reyes of the Gipsy Kings in France.",
  },
  {
    id: 24,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/z26n3rbi_image.png",
    description: "2010 | Istanbul — Kıvanç Öner and George Reyes during soundcheck before the Gipsy Kings concert at Harbiye Open-Air Theatre.",
  },
  {
    id: 25,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/i3bl5f8y_image.png",
    description: "2010 | New York — Kıvanç Öner backstage with Tambo Reyes and Paul Reyes at the Gipsy Kings Heritage concert he organized in New York.",
  },
  {
    id: 26,
    image: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/csobs6iw_image.png",
    description: "2012 | Arles, France — Visiting Arles together with Nicolas Reyes as guests of Chico Bouchikhi, co-founder of the Gipsy Kings.",
  },
];

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#events", label: "Events" },
    { href: "#videos", label: "Videos" },
    { href: "#gallery", label: "Gallery" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-solid py-3" : "nav-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#" className="flex items-center gap-2" data-testid="logo">
          <span className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#C9A14A]">
            PRIVA EVENTS
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm tracking-wider text-[#F5E9DA]/80 hover:text-[#C9A14A] transition-colors"
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary bg-[#C9A14A] text-[#0B0B0C] px-6 py-2.5 text-sm font-medium tracking-wider hover:bg-[#d4af5a]"
            data-testid="nav-book-btn"
          >
            Book Now
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden text-[#F5E9DA] relative z-[100]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu md:hidden absolute top-full left-0 right-0 py-6 px-6" 
          data-testid="mobile-menu"
          style={{ zIndex: 9999 }}
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-lg text-[#F5E9DA]/80 hover:text-[#C9A14A] transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary bg-[#C9A14A] text-[#0B0B0C] px-6 py-3 text-center font-medium tracking-wider mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Book Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section flex items-center justify-center" data-testid="hero-section">
      <div
        className="hero-background"
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      <div className="hero-overlay" />
      <div className="hero-flame-overlay" />

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#C9A14A] mb-6 animate-fadeInUp opacity-0">
          Priva Event &nbsp;|&nbsp; Priva Events Presents
        </p>
        
        <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#F5E9DA] mb-2 animate-fadeInUp opacity-0 stagger-1">
          Patchai Reyes
        </h2>
        
        <p className="font-['Playfair_Display'] italic text-lg md:text-xl text-[#F5E9DA]/70 mb-4 animate-fadeInUp opacity-0 stagger-2">
          Former Member of
        </p>
        
        <h1 className="font-['Playfair_Display'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter text-gradient-flame mb-8 animate-fadeInUp opacity-0 stagger-3">
          GIPSY KINGS
        </h1>
        
        <p className="text-base md:text-lg text-[#F5E9DA]/70 max-w-2xl mx-auto mb-10 animate-fadeInUp opacity-0 stagger-4">
          Book the Gipsy Kings & Patchai Reyes with Priva Event — Live. Passionate. Unforgettable.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp opacity-0 stagger-5">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary border border-[#F5E9DA]/30 text-[#F5E9DA] px-8 py-4 text-sm font-medium tracking-wider inline-flex items-center justify-center gap-2 hover:border-[#C9A14A] hover:text-[#C9A14A]"
            data-testid="hero-book-btn"
          >
            Book This Experience
            <ArrowRight size={18} />
          </a>
          <a
            href="#videos"
            className="btn-primary border border-[#F5E9DA]/30 text-[#F5E9DA] px-8 py-4 text-sm font-medium tracking-wider inline-flex items-center justify-center gap-2 hover:border-[#C9A14A] hover:text-[#C9A14A]"
            data-testid="hero-watch-btn"
          >
            <Play size={18} />
            Watch Performances
          </a>
        </div>
      </div>

      <a
        href="#about"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#C9A14A] animate-float"
        data-testid="scroll-indicator"
      >
        <ChevronDown size={32} />
      </a>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-4">
              The Legacy
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight text-[#F5E9DA] mb-8">
              A Story of Passion <br />
              <span className="text-gradient-flame">& Rhythm</span>
            </h2>
            <div className="section-divider mb-8" />
            <div className="space-y-6 text-[#F5E9DA]/80 leading-relaxed">
              <p>
                Born from the heart of the Gitano tradition in Southern France, Patchai Reyes 
                carries the authentic spirit of flamenco rumba that made the Gipsy Kings a 
                worldwide phenomenon.
              </p>
              <p>
                As a founding member and key architect of the legendary sound that gave us 
                "Bamboléo" and "Volare," Patchai brings decades of passion, mastery, and 
                soul to every performance.
              </p>
              <p>
                Together with his ensemble, he continues to ignite stages across the globe, 
                delivering the raw energy and emotional depth that only true Gipsy heritage 
                can provide.
              </p>
            </div>
            <blockquote className="mt-10 border-l-2 border-[#C9A14A] pl-6 italic text-xl font-['Playfair_Display'] text-[#C9A14A]">
              "Music is not what I do — it is who I am."
              <span className="block text-sm mt-2 text-[#F5E9DA]/60 not-italic font-['Outfit']">
                — Patchai Reyes
              </span>
            </blockquote>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.band}
                alt="Patchai Reyes and the Gipsy Kings"
                className="w-full h-auto object-cover scale-110"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Event Types Section
const EventTypesSection = () => {
  return (
    <section id="events" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f0f10]" data-testid="events-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-4">
            Your Vision, Our Stage
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight text-[#F5E9DA] mb-6">
            Book This Group for <br className="hidden sm:block" />
            <span className="text-gradient-flame">Your Event</span>
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {EVENT_TYPES.map((event) => (
            <div
              key={event.id}
              className="event-card group relative h-[400px] md:h-[500px] overflow-hidden"
              data-testid={`event-card-${event.id}`}
            >
              <img
                src={event.image}
                alt={event.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/60 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#F5E9DA] mb-3">
                  {event.title}
                </h3>
                <p className="text-[#F5E9DA]/70 text-sm md:text-base mb-6 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                  {event.description}
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#C9A14A] text-sm font-medium tracking-wider hover:text-[#FF5A1F] transition-colors"
                  data-testid={`event-cta-${event.id}`}
                >
                  Inquire Now
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Video Card Component
const VideoCard = ({ video }) => {
  // For Instagram videos, open in new tab
  if (video.type === "instagram") {
    return (
      <a 
        href={video.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="video-card relative aspect-video cursor-pointer block"
        data-testid={`video-card-${video.id}`}
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover"
        />
        <div className="play-icon w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
          <Instagram size={28} className="text-white" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-10">
          <h4 className="font-['Playfair_Display'] text-lg md:text-xl text-[#F5E9DA]">
            {video.title}
          </h4>
        </div>
      </a>
    );
  }

  // For YouTube videos, use dialog
  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="video-card relative aspect-video cursor-pointer" data-testid={`video-card-${video.id}`}>
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover"
          />
          <div className="play-icon w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#C9A14A] flex items-center justify-center">
            <Play size={28} className="text-[#0B0B0C] ml-1" fill="#0B0B0C" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-10">
            <h4 className="font-['Playfair_Display'] text-lg md:text-xl text-[#F5E9DA]">
              {video.title}
            </h4>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl w-full p-0 bg-[#0B0B0C] border border-[#2A2A2D]">
        <DialogTitle className="sr-only">{video.title}</DialogTitle>
        <DialogDescription className="sr-only">Watch {video.title} performance video</DialogDescription>
        <div className="aspect-video">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Video Showcase Section
const VideoShowcaseSection = () => {
  return (
    <section id="videos" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="videos-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-4">
            Live Performances
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight text-[#F5E9DA] mb-6">
            Experience the <span className="text-gradient-flame">Magic</span>
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {VIDEOS.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const galleryImages = [
    { id: 1, src: IMAGES.hero, alt: "Live Performance", type: "image" },
    { id: 2, src: IMAGES.performance, alt: "Concert Moment", type: "image" },
    { id: 3, src: "https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/pdfy17nt_20260313_013805775_iOS.jpg", alt: "Performance", type: "image" },
  ];

  return (
    <section id="gallery" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f0f10]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-4">
            Gallery
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight text-[#F5E9DA] mb-6">
            Captured <span className="text-gradient-flame">Moments</span>
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {galleryImages.map((item, index) => (
            <div
              key={item.id}
              className={`gallery-image relative overflow-hidden ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`gallery-image-${item.id}`}
            >
              {item.type === "video" ? (
                <video
                  className={`w-full object-cover bg-black ${
                    index === 0 ? "h-full min-h-[400px] md:min-h-[600px]" : "h-[300px]"
                  }`}
                  controls
                  muted
                  playsInline
                  loop
                  preload="auto"
                >
                  <source src={item.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={item.src}
                  alt={item.alt}
                  className={`w-full object-cover ${
                    index === 0 ? "h-full min-h-[400px] md:min-h-[600px]" : "h-[300px]"
                  }`}
                />
              )}
              <div className="absolute inset-0 border border-white/5 pointer-events-none" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// PRIVA Event Brand Section
const PrivaBrandSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden" data-testid="priva-brand-section">
      <div className="absolute inset-0 bg-gradient-flame opacity-20" />
      
      <div className="max-w-5xl mx-auto text-center relative z-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#F5E9DA]/60 mb-8">
          Curated By
        </p>
        <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight text-[#C9A14A] mb-8">
          PRIVA EVENTS
        </h2>
        <div className="section-divider mx-auto mb-10" />
        <p className="text-lg md:text-xl text-[#F5E9DA]/80 max-w-2xl mx-auto leading-relaxed mb-8">
          Premium event curation and global artist booking. We transform your vision into 
          extraordinary experiences that leave lasting impressions.
        </p>
        <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-center">
          <div>
            <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#C9A14A]">Global</p>
            <p className="text-sm text-[#F5E9DA]/60 mt-1">Event Network</p>
          </div>
          <div>
            <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#C9A14A]">Luxury</p>
            <p className="text-sm text-[#F5E9DA]/60 mt-1">Experience Partner</p>
          </div>
          <div>
            <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#C9A14A]">Premium</p>
            <p className="text-sm text-[#F5E9DA]/60 mt-1">Artist Curation</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Timeline Section for Kıvanç Öner
const TimelineSection = () => {
  return (
    <section id="timeline" className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#0f0f10]" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-3">
            Founder of Priva Event
          </p>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl tracking-tight text-[#F5E9DA] mb-4">
            Kıvanç <span className="text-gradient-flame">Öner</span>
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        {/* Horizontal Scrollable Timeline */}
        <div className="relative">
          <div className="overflow-x-auto pb-6 scrollbar-hide">
            <div className="flex gap-5 min-w-max px-4">
              {TIMELINE_DATA.map((item, index) => (
                <div
                  key={item.id}
                  className="flex flex-col items-center w-[200px] md:w-[240px] flex-shrink-0"
                  data-testid={`timeline-item-${item.id}`}
                >
                  {/* Timeline connector */}
                  <div className="relative w-full flex items-center justify-center mb-4">
                    <div className="absolute left-0 right-0 h-[1px] bg-[#2A2A2D]" />
                    <div className="relative z-10 w-3 h-3 rounded-full bg-[#C9A14A] border-2 border-[#0f0f10]" />
                  </div>
                  
                  {/* Image */}
                  <div className="relative w-full aspect-square overflow-hidden mb-3 border border-[#2A2A2D] group">
                    <img
                      src={item.image}
                      alt={`Timeline ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  
                  {/* Description */}
                  <p className="text-xs md:text-sm text-[#F5E9DA]/80 text-center leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Scroll indicator */}
          {TIMELINE_DATA.length > 3 && (
            <div className="flex justify-center mt-3 gap-2">
              <p className="text-xs text-[#F5E9DA]/40">Scroll to explore →</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f0f10]" data-testid="contact-section">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C9A14A] mb-4">
          Let's Create Something Extraordinary
        </p>
        <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight text-[#F5E9DA] mb-6">
          Get in <span className="text-gradient-flame">Touch</span>
        </h2>
        <div className="section-divider mx-auto mb-12" />
        
        <p className="text-lg text-[#F5E9DA]/70 mb-12 max-w-2xl mx-auto">
          Ready to bring the legendary Gipsy Kings experience to your event? 
          Contact us directly for exclusive booking inquiries.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 px-5 py-3 transition-all duration-300"
            data-testid="contact-whatsapp"
          >
            <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
              <MessageCircle size={18} className="text-white" />
            </div>
            <div className="text-left">
              <p className="text-[10px] text-[#F5E9DA]/60 uppercase tracking-wider">WhatsApp</p>
              <p className="text-sm text-[#F5E9DA] font-medium">{WHATSAPP_NUMBER}</p>
            </div>
          </a>

          <a
            href={`mailto:${EMAIL}`}
            className="group flex items-center gap-3 bg-[#C9A14A]/10 hover:bg-[#C9A14A]/20 border border-[#C9A14A]/30 px-5 py-3 transition-all duration-300"
            data-testid="contact-email"
          >
            <div className="w-9 h-9 rounded-full bg-[#C9A14A] flex items-center justify-center">
              <Mail size={18} className="text-[#0B0B0C]" />
            </div>
            <div className="text-left">
              <p className="text-[10px] text-[#F5E9DA]/60 uppercase tracking-wider">Email</p>
              <p className="text-sm text-[#F5E9DA] font-medium">{EMAIL}</p>
            </div>
          </a>

          <a
            href="https://www.instagram.com/privaevents?igsh=M3djZGFuZWlnOXdm"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-[#E1306C]/10 hover:bg-[#E1306C]/20 border border-[#E1306C]/30 px-5 py-3 transition-all duration-300"
            data-testid="contact-instagram"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
              <Instagram size={18} className="text-white" />
            </div>
            <div className="text-left">
              <p className="text-[10px] text-[#F5E9DA]/60 uppercase tracking-wider">Instagram</p>
              <p className="text-sm text-[#F5E9DA] font-medium">@privaevents</p>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

// Brand Logo Section (below Contact)
const BrandLogoSection = () => {
  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#0B0B0C]" data-testid="brand-logo-section">
      <div className="max-w-4xl mx-auto flex justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/asj2yvwm_image.png" 
          alt="PRIVA EVENTS" 
          className="h-24 md:h-32 lg:h-40 w-auto opacity-30"
        />
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-white/5 bg-[#0B0B0C]" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_gipsy-experience/artifacts/asj2yvwm_image.png" 
              alt="PRIVA EVENTS" 
              className="h-10 md:h-12 w-auto"
              style={{ filter: 'brightness(1.2)' }}
            />
          </div>
          
          <p className="text-sm text-[#F5E9DA]/50">
            © {new Date().getFullYear()} Priva Events. All rights reserved.
          </p>

          <div className="flex items-center gap-6">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F5E9DA]/50 hover:text-[#25D366] transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={20} />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="text-[#F5E9DA]/50 hover:text-[#C9A14A] transition-colors"
              data-testid="footer-email"
            >
              <Mail size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Sticky WhatsApp CTA
const StickyWhatsAppCTA = () => {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="sticky-cta w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] flex items-center justify-center transition-transform duration-300"
      data-testid="sticky-whatsapp-cta"
      aria-label="Contact via WhatsApp"
    >
      <MessageCircle size={28} className="text-white" />
    </a>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-[#0B0B0C]">
      <div className="grain-overlay" />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <EventTypesSection />
        <VideoShowcaseSection />
        <GallerySection />
        <PrivaBrandSection />
        <TimelineSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
